import { useEffect, useState, useMemo, useCallback } from "react";
import React from 'react';
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { buildTotalsRow, exportReportWithPlantHeader, exportReportAsCsvWithPlantHeader } from '../../utils/reportExcelExport';
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import {Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter} from "reactstrap";
import { default as ReactSelect } from "react-select";
import {getDispatchReport,getDispatchReportPDF} from "../../services/ReportServices";
import {  DatePicker } from 'antd';
import { getUserAllowedPlants} from '../../services/PlantServices';
import { getAllCustomerMain} from '../../services/CustomerMainServices';
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import {getAllCustomerSites} from '../../services/CustomerSiteServices';
import { getAllProducts } from "../../services/ProductServices";
import { getAllEquipments } from '../../services/EquipmentServices';
import { getCustomerVehicles } from '../../services/DeliveryChallanServices';
import moment  from 'moment';
import {displayErrorToast,displayError,displayApiError,parseBoolean,parseBooleanToString} from '../../helpers';
import {HandleSelect,ValueContainer,Option,selectedValuesToIdParam,isSelectAllSelected} from "../../components/MultiSelectControls";
import ReportFilterToggleBar from '../../components/ReportFilterToggleBar';
import SettingsIcon from '../../components/SettingsIcon';
import { useReportTableExports } from '../../hooks/useReportTableExports';
import DispatchSummaryChart from '../../components/DispatchSummaryChart';
import { isInactiveRecord, formatQty, formatAmount, sumField } from '../../utils/listSummaryUtils';
import { showCancelledRecordsPopup } from '../../utils/PopupUtils';
const {RangePicker} = DatePicker;

const groupHeaderCellStyle = (row) => (
    row.is_group_header || row.is_total ? { fontWeight: 'bold', color: '#1e3a8a' } : undefined
);

const DISPATCH_REPORT_ALL_COLUMNS = [
    {
        fieldName: 'plant_alias',
        headerName: 'Plant',
        getCellStyle: groupHeaderCellStyle,
    },
    { fieldName: 'salesrep_alias', headerName: 'CSM', getCellStyle: groupHeaderCellStyle },
    { fieldName: 'delivery_challan_no', headerName: 'DC No' },
    { fieldName: 'delivery_challan_date', headerName: 'Date', getCellStyle: groupHeaderCellStyle },
    { fieldName: 'delivery_challan_time', headerName: 'Time' },
    { fieldName: 'status', headerName: 'DC Status' },
    { fieldName: 'customer_name', headerName: 'Customer', getCellStyle: groupHeaderCellStyle },
    { fieldName: 'project_name', headerName: 'Site' },
    { fieldName: 'dc_grade', headerName: 'Grade' },
    { fieldName: 'dc_qty', headerName: 'Qty', align: 'right', getCellStyle: groupHeaderCellStyle },
    { fieldName: 'rate', headerName: 'Rate', align: 'right' },
    { fieldName: 'amount', headerName: 'Amount', align: 'right', getCellStyle: groupHeaderCellStyle },
    { fieldName: 'delivery_mode', headerName: 'Mode' },
    { fieldName: 'vehicle_no', headerName: 'Vehicle No' },
    { fieldName: 'pump', headerName: 'Pump No' },
    { fieldName: 'gang', headerName: 'Gang' },
    { fieldName: 'total_km', headerName: 'Total Km' },
    { fieldName: 'invoice_number', headerName: 'Invoice No.' },
];

const formatTwoDecimals = (value) => {
    if (value === '' || value === null || value === undefined) {
        return '';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return '';
    }
    return num.toFixed(2);
};

const formatAbstractPlantHeader = (label = '') => (
    String(label).replace(/(\d{2})-(\d{2})/g, '$1\u2011$2')
);

const getAbstractPlantColumnWidth = (label = '') => Math.max(112, Math.min(240, label.length * 7));

const formatAbstractQtyDisplay = (value) => {
    const num = Number(value || 0);
    if (!num || num <= 0) {
        return '';
    }
    return num.toFixed(2);
};

const mapPlantwiseAbstractRow = (row, cols) => {
    const mapped = {
        id: row.id,
        delivery_challan_date: row.delivery_challan_date,
        total_qty: formatAbstractQtyDisplay(row.total_qty),
        is_total: !!row.is_total,
    };
    cols.forEach((col) => {
        mapped[col.field] = formatAbstractQtyDisplay(row[col.field]);
    });
    return mapped;
};

const orderPlantwiseAbstractRows = (rows) => {
    const totalRow = rows.find((row) => row.is_total);
    const detailRows = rows.filter((row) => !row.is_total);
    return totalRow ? [totalRow, ...detailRows] : detailRows;
};

const getCondensedRate = (qty, amount) => {
    const quantity = Number(qty || 0);
    if (!quantity) {
        return '';
    }
    return formatTwoDecimals(Number(amount || 0) / quantity);
};

const DISPATCH_CONDENSED_SITE_GRADE_MODE_QTY_AMOUNT = [
    { fieldName: 'project_name', headerName: 'Site' },
    { fieldName: 'dc_grade', headerName: 'Grade' },
    { fieldName: 'delivery_mode', headerName: 'Mode' },
    { fieldName: 'dc_qty', headerName: 'Qty', align: 'right', getCellStyle: groupHeaderCellStyle },
    { fieldName: 'rate', headerName: 'Rate', align: 'right', getCellStyle: groupHeaderCellStyle },
    { fieldName: 'amount', headerName: 'Amount', align: 'right', getCellStyle: groupHeaderCellStyle },
];

const CONDENSED_GROUP_LEAD = {
    datewise: { field: 'delivery_challan_date', label: 'Date' },
    customerwise: { field: 'customer_name', label: 'Customer' },
    marketingwise: { field: 'salesrep_name', label: 'Marketing' },
};

const DISPATCH_SUMMARY_MODES = {
    plantwise: { field: 'plant_alias', label: 'Plant', headerName: 'Plant' },
    marketingwise: { field: 'salesrep_alias', label: 'Marketing', headerName: 'Marketing' },
    customerwise: { field: 'customer_name', label: 'Customer', headerName: 'Customer' },
    productwise: { field: 'dc_grade', label: 'Product', headerName: 'Product' },
};

const buildPeriodDateKeys = (fromDate, toDate) => {
    const keys = [];
    const cursor = fromDate.clone().startOf('day');
    const end = toDate.clone().startOf('day');
    while (cursor.isSameOrBefore(end, 'day')) {
        keys.push(cursor.format('DD-MM-YYYY'));
        cursor.add(1, 'day');
    }
    return keys;
};

const summaryDateField = (dateStr) => `d_${String(dateStr || '').replace(/-/g, '_')}`;

const formatSummaryQty = (value) => {
    if (!value) {
        return '';
    }
    return formatTwoDecimals(value);
};

/** Summary matrix: rows = plant/marketing/customer/product, columns = period dates, values = qty. */
const buildDispatchSummaryReport = (rows, fromDate, toDate, summaryMode) => {
    const modeConfig = DISPATCH_SUMMARY_MODES[summaryMode] || DISPATCH_SUMMARY_MODES.plantwise;
    const dateKeys = buildPeriodDateKeys(fromDate, toDate);
    const dateFields = dateKeys.map(summaryDateField);
    const entityMap = new Map();

    rows.forEach((row) => {
        const entity = row[modeConfig.field] || row.plant_name || row.salesrep_name || 'Unassigned';
        if (!entityMap.has(entity)) {
            const record = {
                id: `summary-${summaryMode}-${entity}`,
                entity_name: entity,
                total: 0,
            };
            dateFields.forEach((field) => {
                record[field] = 0;
            });
            entityMap.set(entity, record);
        }

        const record = entityMap.get(entity);
        const field = summaryDateField(row.delivery_challan_date);
        if (!Object.prototype.hasOwnProperty.call(record, field)) {
            return;
        }
        const increment = Number(row.dc_qty || 0);
        record[field] += increment;
        record.total += increment;
    });

    const entityRecords = Array.from(entityMap.values())
        .sort((left, right) => compareText(left.entity_name, right.entity_name));

    const totals = {
        id: 'summary-total',
        entity_name: 'Total',
        is_total: true,
        total: 0,
    };
    dateFields.forEach((field) => {
        totals[field] = 0;
    });
    entityRecords.forEach((record) => {
        dateFields.forEach((field) => {
            totals[field] += Number(record[field] || 0);
        });
        totals.total += Number(record.total || 0);
    });

    const records = [
        {
            ...totals,
            ...Object.fromEntries(
                dateFields.map((field) => [field, formatSummaryQty(totals[field])])
            ),
            total: formatSummaryQty(totals.total),
        },
        ...entityRecords.map((record) => {
            const next = { ...record };
            dateFields.forEach((field) => {
                next[field] = formatSummaryQty(record[field]);
            });
            next.total = formatSummaryQty(record.total);
            return next;
        }),
    ];

    const columns = [
        { fieldName: 'entity_name', headerName: modeConfig.headerName, minSize: 140 },
        ...dateKeys.map((dateKey) => ({
            fieldName: summaryDateField(dateKey),
            headerName: dateKey,
            align: 'right',
            minSize: 92,
            headerStyle: { whiteSpace: 'nowrap' },
        })),
        {
            fieldName: 'total',
            headerName: 'Total',
            align: 'right',
            minSize: 88,
            getCellStyle: () => ({ fontWeight: 700, color: '#0f255f' }),
        },
    ];

    const chartRows = entityRecords.map((record) => ({
        label: record.entity_name,
        total: Number(record.total || 0),
    }));

    return { columns, records, chartRows, modeLabel: modeConfig.label };
};

const compareText = (left, right) => String(left || '').localeCompare(
    String(right || ''),
    undefined,
    { sensitivity: 'base', numeric: true },
);

const parseDispatchDateValue = (row) => {
    if (row?.dc_date_original) {
        const parsed = moment(row.dc_date_original);
        if (parsed.isValid()) {
            return parsed.valueOf();
        }
    }
    const parsed = moment(row?.delivery_challan_date, 'DD-MM-YYYY', true);
    return parsed.isValid() ? parsed.valueOf() : 0;
};

const parseDispatchDateKey = (value) => {
    const parsed = moment(value, 'DD-MM-YYYY', true);
    return parsed.isValid() ? parsed.valueOf() : 0;
};

const compareDcNumber = (left, right) => {
    const leftText = String(left ?? '').trim();
    const rightText = String(right ?? '').trim();
    const leftNumeric = Number(leftText);
    const rightNumeric = Number(rightText);
    const leftIsNumeric = leftText !== '' && !Number.isNaN(leftNumeric);
    const rightIsNumeric = rightText !== '' && !Number.isNaN(rightNumeric);
    if (leftIsNumeric && rightIsNumeric) {
        return leftNumeric - rightNumeric;
    }
    return compareText(leftText, rightText);
};

const compareDispatchRows = (left, right) => {
    const dateDiff = parseDispatchDateValue(left) - parseDispatchDateValue(right);
    if (dateDiff !== 0) {
        return dateDiff;
    }
    const dcNoDiff = compareDcNumber(left.delivery_challan_no, right.delivery_challan_no);
    if (dcNoDiff !== 0) {
        return dcNoDiff;
    }
    const customerDiff = compareText(left.customer_name, right.customer_name);
    if (customerDiff !== 0) {
        return customerDiff;
    }
    const siteDiff = compareText(left.project_name, right.project_name);
    if (siteDiff !== 0) {
        return siteDiff;
    }
    const gradeDiff = compareText(left.dc_grade, right.dc_grade);
    if (gradeDiff !== 0) {
        return gradeDiff;
    }
    return compareText(left.delivery_mode, right.delivery_mode);
};

const sortDispatchRows = (rows) => [...rows].sort(compareDispatchRows);

const compareGroupKeys = (reportFormat, left, right) => {
    if (reportFormat === 'datewise') {
        return parseDispatchDateKey(left) - parseDispatchDateKey(right);
    }
    return compareText(left, right);
};

const getCondensedColumns = (reportFormat) => {
    const columns = [
        {
            fieldName: 'delivery_challan_date',
            headerName: 'Date',
            getCellStyle: groupHeaderCellStyle,
        },
        {
            fieldName: 'customer_name',
            headerName: 'Customer',
            getCellStyle: groupHeaderCellStyle,
        },
    ];

    if (reportFormat === 'marketingwise') {
        columns.push({
            fieldName: 'salesrep_name',
            headerName: 'Marketing',
            getCellStyle: groupHeaderCellStyle,
        });
    }

    columns.push(...DISPATCH_CONDENSED_SITE_GRADE_MODE_QTY_AMOUNT);
    return columns;
};

const aggregateCondensedDispatchRows = (rows) => {
    const groups = new Map();
    const orderedKeys = [];

    rows.forEach((row) => {
        const mode = (row.delivery_mode || '').toUpperCase();
        const key = [
            row.delivery_challan_date || '',
            row.customer_name || '',
            row.project_name || '',
            row.dc_grade || '',
            mode,
        ].join('|||');

        if (!groups.has(key)) {
            groups.set(key, {
                id: `condensed-${orderedKeys.length}`,
                delivery_challan_date: row.delivery_challan_date,
                dc_date_original: row.dc_date_original,
                customer_name: row.customer_name,
                customer_id: row.customer_id,
                project_name: row.project_name,
                dc_grade: row.dc_grade,
                delivery_mode: mode,
                salesrep_id: row.salesrep_id,
                salesrep_name: row.salesrep_name,
                dc_qty: 0,
                amount: 0,
            });
            orderedKeys.push(key);
        }

        const agg = groups.get(key);
        agg.dc_qty += Number(row.dc_qty || 0);
        agg.amount += Number(row.amount || 0);
    });

    return orderedKeys.map((key) => {
        const row = groups.get(key);
        const dcQty = Number(row.dc_qty.toFixed(2));
        const amount = Number(row.amount.toFixed(2));
        return {
            ...row,
            dc_qty: formatTwoDecimals(dcQty),
            amount: formatTwoDecimals(amount),
            rate: getCondensedRate(dcQty, amount),
        };
    });
};

function DispatchReportTable() 
{
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false); 
    const [plants, setPlants] = React.useState([]);
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedSalesrep, setSelectedSalesRep] = useState([]);
    const [salesreps, setSalesreps] = useState([]);
    const [selectedSite, setSelectedSite] = useState([]);
    const [sites, setSites] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState([]);
    const [grades, setGrades] = useState([]);
    const [inputs, setInputs] = useState({ report_format: 'datewise' });
    const [reportSettings, setReportSettings] = useState({ report_type: 'detailed' });
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [settingsDraft, setSettingsDraft] = useState({
        report_type: 'detailed',
        selectedPlant: [],
        selectedSalesrep: [],
        selectedVehicle: [],
    });
    const [totalDcQty, setTotalDcQty] = useState('0.00');
    const [totalAmount, setTotalAmount] = useState('0.00');
    const [cancelledDcQty, setCancelledDcQty] = useState('0.00');
    const [cancelledDcCount, setCancelledDcCount] = useState(0);
    const [plantColumns, setPlantColumns] = useState([]);
    const [displayReportFormat, setDisplayReportFormat] = useState('datewise');
    const [summaryColumns, setSummaryColumns] = useState([]);
    const [summaryChartRows, setSummaryChartRows] = useState([]);
    const [summaryModeLabel, setSummaryModeLabel] = useState('Plant');
    const isSummaryReport = reportSettings.report_type === 'summary';
    const isPlantwiseAbstract = !isSummaryReport && inputs.report_format === 'plantwise_abstract';
    const [filtersVisible, setFiltersVisible] = useState(true);

    // Initial state for from_date and to_date
    const initialFromDate = moment().startOf('month');
    const initialToDate =  moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);
    const handleDateChange = (values) => {
        if ((values) && (values.length === 2)) {
        setFromDate(values[0]);
        setToDate(values[1]);
        }
    };

    React.useEffect(() => {
        getUserAllowedPlants(cookies)
        .then (
            plantList => {
                const plantOptions = plantList.plant_list.map(
                    plant => ({ value: plant.id, label: plant.plant_alias })
                );
                setPlants(plantOptions);
                setSelectedPlant([
                    { value: 'select-all', label: 'Select All plants' },
                    ...plantOptions,
                ]);
            }
        )
    }, []);

    React.useEffect(() => {
        getAllCustomerMain(cookies)
        .then (
            customerList => {
                const customers = customerList.customer_list.map(
                    customer => {
                        return { value: customer.id, label: customer.name }
                    }
                );
                setCustomers(customers);
                setSelectedCustomer(values =>[...values,...customers, ...customersWithSelectAll]);
            }
        )
    }, []);

    React.useEffect(() => {
    getAllCustomerSites(cookies)
    .then (
        siteList => {
            const sites = siteList.consignee_list.map(
                site => {
                    return { value: site.id, label: site.name }
                }
            );
            setSites(sites);
            setSelectedSite(values =>[...values,...sites, ...sitesWithSelectAll]);
        }
    )
    }, []);

    React.useEffect(() => {
        getAllSalesRepresentatives(cookies)
        .then (
            salesRepList => {
                const salesrep = salesRepList.salesrep_data.map(
                sales => {
                        return { value: sales.id, label: sales.entity_name }
                    }
                );
                //console.log(JSON.stringify(salesRepList)+"salesRepList")
                setSalesreps(salesrep);
                setSelectedSalesRep(values =>[...values,...salesrep, ...salesrepsWithSelectAll]);
            }
        )
    }, []);

    React.useEffect(() => {
        getCustomerVehicles(cookies)
        .then (
            equipment => {
                const customer_vehicles = equipment.customer_vehicles_list.map(
                    equip => {
                        return { value: equip, label: equip}
                    }
                );
                setVehicles(customer_vehicles);
                setSelectedVehicle(customer_vehicles);
            }
        )
    }, []);

    React.useEffect(() => {
        getAllEquipments(cookies)
        .then (
            equipmentObject => {
                const equipments = equipmentObject.equipment_list.filter(obj => ((obj.equip_type.name === 'TRANSIT MIXER'))).map(
                    equipment => {
                        return { value: equipment.id, label: equipment.equip_name }
                    }
                );
                setVehicles(values =>[...values, ...equipments]);
                setSelectedVehicle(values =>[...values,...equipments, ...vehiclesWithSelectAll]);
            }
        )
    }, []);

    React.useEffect(() => {
        getAllProducts(cookies)
        .then (
            productList => {
                const products = productList.product_list.map(
                    product => {
                        return { value: product.id, label: product.name}
                    }
                )
                setGrades(products);
                setSelectedGrade(values =>[...values,...products, ...gradesWithSelectAll]);
            }
        )
    }, []);

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setInputs(values => ({...values, [name]: value}));
        if (name === 'report_format') {
            setData(InitialData);
            setPlantColumns([]);
            setSummaryColumns([]);
            setSummaryChartRows([]);
            setTotalDcQty('0.00');
            setTotalAmount('0.00');
            setCancelledDcQty('0.00');
            setCancelledDcCount(0);
        }
    }

    // Add a "Select All" option to the plants array
    const plantsWithSelectAll = useMemo(() => ([
        { value: "select-all", label: "Select All plants" },
        ...plants,
    ]), [plants]);

    // Add a "Select All" option to the customers array
    const customersWithSelectAll = [
        { value: "select-all", label: "Select All customers" },
        ...customers,
    ];
    
    // Add a "Select All" option to the sites array
    const sitesWithSelectAll = [
        { value: "select-all", label: "Select All sites" },
        ...sites,
    ];
    
    // Add a "Select All" option to the salesreps array
    const salesrepsWithSelectAll = useMemo(() => ([
        { value: "select-all", label: "Select All sales representatives" },
        ...salesreps,
    ]), [salesreps]);
    
    // Add a "Select All" option to the vehicles array
    const vehiclesWithSelectAll = useMemo(() => ([
        { value: "select-all", label: "Select All vehicles" },
        ...vehicles,
    ]), [vehicles]);

    // Add a "Select All" option to the grades array
    const gradesWithSelectAll = [
        { value: "select-all", label: "Select All grades" },
        ...grades,
    ];
      
    const onCustomerSelect = (selected) => {
        HandleSelect(selected,selectedCustomer, setSelectedCustomer, customers, customersWithSelectAll);
    };
    
    const onSiteSelect = (selected) => {
        HandleSelect(selected,selectedSite, setSelectedSite, sites, sitesWithSelectAll);
    };
    
    const onGradeSelect = (selected) => {
        HandleSelect(selected,selectedGrade, setSelectedGrade, grades, gradesWithSelectAll);
    };

    const areAllPlantsSelected = () => {
        if (isSelectAllSelected(selectedPlant)) {
            return true;
        }
        const realPlants = plants.filter((plant) => plant.value !== 'select-all');
        if (!realPlants.length) {
            return false;
        }
        return realPlants.every((plant) =>
            selectedPlant.some((selected) => selected.value === plant.value),
        );
    };

    const getSelectedPlantsForReport = () => {
        const realPlants = plants.filter((plant) => plant.value !== 'select-all');
        if (areAllPlantsSelected()) {
            return realPlants;
        }
        return selectedPlant.filter((plant) => plant.value !== 'select-all');
    };

    const selectedAbstractPlantColumns = useMemo(() => {
        if (!isPlantwiseAbstract) {
            return [];
        }
        return getSelectedPlantsForReport()
            .map((plant) => ({
                plant_id: plant.value,
                label: plant.label,
                field: `plant_${plant.value}`,
            }))
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlantwiseAbstract, selectedPlant, plants]);

    const areAllCustomersSelected = () => {
        if (isSelectAllSelected(selectedCustomer)) {
            return true;
        }
        return customers.every((customer) =>
            selectedCustomer ? selectedCustomer.some((selected) => selected.value === customer.value) : false
        );
    };

    const areAllConsigneesSelected = () => {
        if (isSelectAllSelected(selectedSite)) {
            return true;
        }
        return sites.every((consignee) =>
        selectedSite ? selectedSite.some((selected) => selected.value === consignee.value) : false
        );
    };

    const areAllSalesRepsSelected = () => {
        if (isSelectAllSelected(selectedSalesrep)) {
            return true;
        }
        return salesreps.every((salesrep) =>
        selectedSalesrep ? selectedSalesrep.some((selected) => selected.value === salesrep.value) : false
        );
    };
    
    const areAllVehiclesSelected = () => {
        if (isSelectAllSelected(selectedVehicle)) {
            return true;
        }
        return vehicles.every((vehicle) =>
        selectedVehicle ? selectedVehicle.some((selected) => selected.value === vehicle.value) : false
        );
    };
    
    const areAllGradesSelected = () => {
        if (isSelectAllSelected(selectedGrade)) {
            return true;
        }
        return grades.every((grade) =>
        selectedGrade ? selectedGrade.some((selected) => selected.value === grade.value) : false
        );
    };

    const hasSelectedPlants = () => (
        areAllPlantsSelected()
        || selectedPlant.some((plant) => plant.value !== 'select-all')
    );

    const hasSelectedSalesReps = () => (
        areAllSalesRepsSelected()
        || selectedSalesrep.some((salesrep) => salesrep.value !== 'select-all')
    );

    const hasSelectedVehicles = () => (
        areAllVehiclesSelected()
        || selectedVehicle.some((vehicle) => vehicle.value !== 'select-all')
    );

    const openReportSettings = () => {
        setSettingsDraft({
            report_type: reportSettings.report_type,
            selectedPlant: selectedPlant,
            selectedSalesrep: selectedSalesrep,
            selectedVehicle: selectedVehicle,
        });
        setShowSettingsModal(true);
    };

    const applyReportSettings = () => {
        const draftPlants = settingsDraft.selectedPlant || [];
        const draftSalesreps = settingsDraft.selectedSalesrep || [];
        const draftVehicles = settingsDraft.selectedVehicle || [];
        const hasPlants = draftPlants.some((plant) => plant.value === 'select-all')
            || draftPlants.some((plant) => plant.value !== 'select-all');
        const hasSalesreps = draftSalesreps.some((salesrep) => salesrep.value === 'select-all')
            || draftSalesreps.some((salesrep) => salesrep.value !== 'select-all');
        const hasVehicles = draftVehicles.some((vehicle) => vehicle.value === 'select-all')
            || draftVehicles.some((vehicle) => vehicle.value !== 'select-all');
        if (!hasPlants) {
            Swal.fire('Please select at least one plant in report settings.', '', 'info');
            return;
        }
        if (!hasSalesreps) {
            Swal.fire('Please select at least one sales rep in report settings.', '', 'info');
            return;
        }
        if (!hasVehicles) {
            Swal.fire('Please select at least one vehicle in report settings.', '', 'info');
            return;
        }
        setReportSettings({ report_type: settingsDraft.report_type });
        setSelectedPlant(draftPlants);
        setSelectedSalesRep(draftSalesreps);
        setSelectedVehicle(draftVehicles);
        if (settingsDraft.report_type === 'summary') {
            setInputs((current) => ({
                ...current,
                report_format: DISPATCH_SUMMARY_MODES[current.report_format]
                    ? current.report_format
                    : 'plantwise',
            }));
            setData({ records: [] });
            setSummaryColumns([]);
            setSummaryChartRows([]);
        } else if (DISPATCH_SUMMARY_MODES[inputs.report_format]) {
            setInputs((current) => ({ ...current, report_format: 'datewise' }));
            setSummaryColumns([]);
            setSummaryChartRows([]);
        }
        setShowSettingsModal(false);
    };

    const onSettingsPlantSelect = (selected) => {
        HandleSelect(selected, settingsDraft.selectedPlant, (value) => {
            setSettingsDraft((current) => ({ ...current, selectedPlant: value }));
        }, plants, plantsWithSelectAll);
    };

    const onSettingsSalesRepSelect = (selected) => {
        HandleSelect(selected, settingsDraft.selectedSalesrep, (value) => {
            setSettingsDraft((current) => ({ ...current, selectedSalesrep: value }));
        }, salesreps, salesrepsWithSelectAll);
    };

    const onSettingsVehicleSelect = (selected) => {
        HandleSelect(selected, settingsDraft.selectedVehicle, (value) => {
            setSettingsDraft((current) => ({ ...current, selectedVehicle: value }));
        }, vehicles, vehiclesWithSelectAll);
    };

    const mapDispatchReportRow = (dc) => ({
        plant_name: dc.plant_name,
        plant_alias: dc.plant_alias,
        production_plant_name: dc.production_plant_name,
        id: dc.id,
        customer_id: dc.customer_id,
        salesrep_id: dc.salesrep_id,
        batch_no: dc.batch_no,
        delivery_challan_no: dc.delivery_challan_no,
        delivery_challan_date: dc.delivery_challan_date,
        dc_date_original: dc.dc_date_original,
        delivery_challan_time: dc.delivery_challan_time,
        status: dc.status || 'Active',
        sales_order_no: dc.sales_order_no,
        purchase_order_no: dc.purchase_order_no,
        invoice_number: dc.invoice_number,
        customer_name: dc.customer_name,
        project_name: dc.project_name,
        dc_grade: dc.dc_grade,
        dc_qty: formatTwoDecimals(dc.dc_qty),
        rate: formatTwoDecimals(dc.rate),
        amount: formatTwoDecimals(dc.amount),
        delivery_mode: (dc.delivery_mode || '').toUpperCase(),
        vehicle_no: dc.vehicle_no,
        salesrep_name: dc.salesrep_name,
        salesrep_alias: dc.salesrep_alias,
        driver_name: dc.driver_name,
        accepted_qty: dc.accepted_qty,
        customer_dc_status: dc.customer_dc_status,
        total_km: dc.total_km,
        net_weight: dc.net_weight,
        gang: dc.gang,
        pump: dc.pump,
    });

    const buildGroupedReportRows = (rows, reportFormat, reportType = reportSettings.report_type) => {
        if (['all', 'plantwise_abstract'].includes(reportFormat) || !rows.length) {
            return rows;
        }

        const groupConfig = CONDENSED_GROUP_LEAD;
        const config = groupConfig[reportFormat];
        if (!config) {
            return rows;
        }

        const orderedKeys = [];
        const groups = new Map();

        rows.forEach((row) => {
            const key = row[config.field] || 'Unassigned';
            if (!groups.has(key)) {
                groups.set(key, []);
                orderedKeys.push(key);
            }
            groups.get(key).push(row);
        });

        const groupedRows = [];
        orderedKeys.sort((left, right) => compareGroupKeys(reportFormat, left, right));
        orderedKeys.forEach((key) => {
            const groupRows = sortDispatchRows(groups.get(key) || []);
            const groupQty = groupRows.reduce((sum, row) => sum + Number(row.dc_qty || 0), 0);
            const groupAmount = groupRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
            const groupLabelField = reportType === 'condensed'
                ? 'delivery_challan_date'
                : 'plant_alias';
            groupedRows.push({
                id: `group-${config.label}-${key}`,
                is_group_header: true,
                [groupLabelField]: key,
                dc_qty: groupQty.toFixed(2),
                amount: groupAmount.toFixed(2),
                rate: getCondensedRate(groupQty, groupAmount),
            });
            groupedRows.push(...groupRows);
        });
        return groupedRows;
    };

    const Home = () => {
        navigate('/Home')
    }

    const detailColumns = useMemo(() => {
        if (isSummaryReport) {
            return summaryColumns.length
                ? summaryColumns
                : [{ fieldName: 'entity_name', headerName: 'Name' }];
        }
        if (reportSettings.report_type === 'condensed') {
            return getCondensedColumns(displayReportFormat);
        }
        return DISPATCH_REPORT_ALL_COLUMNS;
    }, [reportSettings.report_type, displayReportFormat, isSummaryReport, summaryColumns]);

    const abstractColumns = useMemo(
        () => [
            {
                fieldName: 'delivery_challan_date',
                headerName: 'Date',
                getCellStyle: groupHeaderCellStyle,
                minSize: 92,
                headerStyle: { whiteSpace: 'nowrap' },
            },
            ...(selectedAbstractPlantColumns.length ? selectedAbstractPlantColumns : plantColumns).map((col) => ({
                fieldName: col.field,
                headerName: formatAbstractPlantHeader(col.label),
                align: 'right',
                getCellStyle: groupHeaderCellStyle,
                minSize: getAbstractPlantColumnWidth(col.label),
                headerStyle: { whiteSpace: 'nowrap' },
            })),
            {
                fieldName: 'total_qty',
                headerName: 'Total',
                align: 'right',
                getCellStyle: groupHeaderCellStyle,
                minSize: 88,
                headerStyle: { whiteSpace: 'nowrap' },
            },
        ],
        [selectedAbstractPlantColumns, plantColumns],
    );

    const columns = isPlantwiseAbstract ? abstractColumns : detailColumns;

    const { onImage: handleImageExport } = useReportTableExports({
        columns,
        records: data.records,
        title: isSummaryReport
            ? `Dispatch Report - Summary (${summaryModeLabel}wise)`
            : (isPlantwiseAbstract ? 'Dispatch Report - Plantwise Abstract' : 'Dispatch Report'),
        filename: 'dispatch_report',
    });
   
    const handlePDFExport = (event) => {
        // console.log('PDF Export');
        exportAsPDF(
            'Dispatch Report',
            columns
            .filter(col => ['Date', 'DC Number','Time','Customer Name','Site/Project Name','DC Grade','DC Qty','Rate','Amount','Vehicle No','Marketting'].includes(col.headerName))
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(dc => ({
                delivery_challan_no: dc.delivery_challan_no,
                delivery_challan_date: dc.delivery_challan_date,
                delivery_challan_time: dc.delivery_challan_time,
                customer_name:dc.customer_name,
                project_name:dc.project_name,
                salesrep_name: dc.salesrep_name,
                vehicle_no: dc.vehicle_no,
                dc_grade:  dc.dc_grade,
                dc_qty: dc.dc_qty,
                rate: dc.rate,
                amount:  dc.amount
            })),
            'Dispatch_report.pdf'
        );
    };

    const constructFormData = () => {
        const selectedPlants = getSelectedPlantsForReport();
        const plantIds = isPlantwiseAbstract
            ? selectedPlants.map((plant) => plant.value).join(',')
            : selectedValuesToIdParam(selectedPlant);
        return {
            plant_ids: plantIds,
            customer_ids: selectedValuesToIdParam(selectedCustomer),
            consignee_ids: selectedValuesToIdParam(selectedSite),
            sales_rep_ids: selectedValuesToIdParam(selectedSalesrep),
            vehicle_ids: selectedValuesToIdParam(selectedVehicle),
            product_ids: selectedValuesToIdParam(selectedGrade),
            from_date:fromDate.format('DD-MM-YYYY'),
            upto_date:toDate.format('DD-MM-YYYY'),
            report_format: isSummaryReport ? 'all' : inputs.report_format,
            report_type: isSummaryReport ? 'detailed' : (reportSettings.report_type || 'detailed'),
          }
    }

    const handleGenerate = () => {
        const formData = constructFormData();
        if (!hasSelectedPlants()) {
            Swal.fire('Please select at least one plant in report settings.', '', 'info');
            return;
        }
        if (!hasSelectedSalesReps()) {
            Swal.fire('Please select at least one sales rep in report settings.', '', 'info');
            return;
        }
        if (!hasSelectedVehicles()) {
            Swal.fire('Please select at least one vehicle in report settings.', '', 'info');
            return;
        }
        if ((formData.plant_ids) && (formData.customer_ids) && (formData.consignee_ids) && (formData.sales_rep_ids) &&
        (formData.vehicle_ids) && (formData.product_ids) &&
        (formData.from_date) && (formData.upto_date) && (formData.report_format))
        {
            setIsLoading(true);
            getDispatchReport(cookies, formData)
            .then(response => {
                if (formData.report_format === 'plantwise_abstract') {
                    const cols = selectedAbstractPlantColumns.length
                        ? selectedAbstractPlantColumns
                        : (response.plant_columns || []);
                    setPlantColumns(cols);
                    const tableData = orderPlantwiseAbstractRows(
                        (response.dispatch_report_list || []).map((row) => (
                            mapPlantwiseAbstractRow(row, cols)
                        )),
                    );
                    setData({ records: tableData });
                    setDisplayReportFormat(formData.report_format);
                    setTotalDcQty(Number(response.total_dc_qty || 0).toFixed(2));
                    setTotalAmount('0.00');
                    setCancelledDcQty('0.00');
                    setCancelledDcCount(0);
                    setFiltersVisible(false);
                    setIsLoading(false);
                    return;
                }
                setPlantColumns([]);
                let detailRows = sortDispatchRows(
                    (response.dispatch_report_list || []).map(mapDispatchReportRow),
                );
                if (formData.report_type === 'condensed') {
                    detailRows = sortDispatchRows(aggregateCondensedDispatchRows(detailRows));
                }
                const tableData = buildGroupedReportRows(detailRows, formData.report_format);
                setData({ records: tableData });
                setDisplayReportFormat(formData.report_format);
                const activeDetailRows = detailRows.filter((row) => !isInactiveRecord(row));
                const cancelledDetailRows = detailRows.filter((row) => isInactiveRecord(row));
                const qtyTotal = activeDetailRows.reduce((sum, row) => sum + Number(row.dc_qty || 0), 0);
                const amountTotal = activeDetailRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
                const cancelledQty = cancelledDetailRows.reduce((sum, row) => sum + Number(row.dc_qty || 0), 0);
                setTotalDcQty(qtyTotal.toFixed(2));
                setTotalAmount(amountTotal.toFixed(2));
                setCancelledDcQty(cancelledQty.toFixed(2));
                setCancelledDcCount(cancelledDetailRows.length);
                setFiltersVisible(false);
                setIsLoading(false);
            }).catch((error) => {
                displayApiError(error);
                setIsLoading(false);
            });
        }
        else
        {
            setData({ records: [] });
            setTotalDcQty('0.00');
            setTotalAmount('0.00');
            setCancelledDcQty('0.00');
            setCancelledDcCount(0);
            Swal.fire("plant,customer,consignee,sales_rep,vehicle,product,from_date,upto_date,report format must not be empty.", "", "info");
        }
    };

    const showCancelledDcDetails = useCallback(() => {
        const cancelledRows = (data.records || []).filter(
            (row) => !row.is_group_header && !row.is_total && isInactiveRecord(row),
        );
        showCancelledRecordsPopup({
            title: `Cancelled Delivery Challans (${cancelledRows.length})`,
            emptyTitle: 'Cancelled DCs',
            emptyMessage: 'No cancelled delivery challans in the current report.',
            columns: [
                { key: 'delivery_challan_no', label: 'DC No', align: 'center' },
                { key: 'delivery_challan_date', label: 'Date', align: 'center' },
                { key: 'customer_name', label: 'Customer', align: 'left' },
                { key: 'project_name', label: 'Site', align: 'left' },
                { key: 'dc_grade', label: 'Grade', align: 'left' },
                { key: 'vehicle_no', label: 'Vehicle', align: 'left' },
                { key: 'dc_qty', label: 'Qty', align: 'right' },
                { key: 'amount', label: 'Amount', align: 'right' },
            ],
            rows: cancelledRows.map((row) => ({
                delivery_challan_no: row.delivery_challan_no,
                delivery_challan_date: row.delivery_challan_date,
                customer_name: row.customer_name,
                project_name: row.project_name,
                dc_grade: row.dc_grade,
                vehicle_no: row.vehicle_no,
                dc_qty: formatQty(row.dc_qty),
                amount: formatAmount(row.amount),
            })),
            footerRow: {
                delivery_challan_no: 'Total',
                delivery_challan_date: '',
                customer_name: '',
                project_name: '',
                dc_grade: '',
                vehicle_no: `${cancelledRows.length} DC`,
                dc_qty: formatQty(sumField(cancelledRows, 'dc_qty')),
                amount: formatAmount(sumField(cancelledRows, 'amount')),
            },
        });
    }, [data.records]);

    const handleSubmit=(event)=>
    {
        event.preventDefault();
        handleGenerate();
    }

        const handleDownloadPdf = () => {
            const inputs = constructFormData();
            if (!hasSelectedPlants()) {
                Swal.fire('Please select at least one plant in report settings.', '', 'info');
                return;
            }
            if (!hasSelectedSalesReps()) {
                Swal.fire('Please select at least one sales rep in report settings.', '', 'info');
                return;
            }
            if (!hasSelectedVehicles()) {
                Swal.fire('Please select at least one vehicle in report settings.', '', 'info');
                return;
            }
            if ((inputs.plant_ids) && (inputs.customer_ids) && (inputs.consignee_ids) && (inputs.sales_rep_ids) &&
            (inputs.vehicle_ids) && (inputs.product_ids) &&
            (inputs.from_date) && (inputs.upto_date)&& (inputs.report_format))
            {
                setIsLoading(true);
                getDispatchReportPDF(cookies, inputs)
                .then(response => {
                    openPdfResponse(response, {
                        title: 'Dispatch Report',
                        downloadFileName: 'Dispatch_report.pdf',
                    });
                })
                .catch((error) => {
                    displayApiError(error, 'PDF download failed');
                })
                .finally(() => {
                    setIsLoading(false);
                });
            }
            else
            {
                Swal.fire("plant,customer,consignee,sales_rep,vehicle,product,from_date,upto_date,report format must not be empty.", "", "info");
            }
        };

        const mapPlantwiseAbstractRowForExport = (row, cols) => {
            const mapped = {
                id: row.id,
                delivery_challan_date: row.delivery_challan_date,
                total_qty: Number(row.total_qty || 0),
                is_total: !!row.is_total,
            };
            cols.forEach((col) => {
                mapped[col.field] = Number(row[col.field] || 0);
            });
            return mapped;
        };

        const canExportDispatch = (formData) => {
            if (!hasSelectedPlants()) {
                Swal.fire('Please select at least one plant in report settings.', '', 'info');
                return false;
            }
            if (!hasSelectedSalesReps()) {
                Swal.fire('Please select at least one sales rep in report settings.', '', 'info');
                return false;
            }
            if (!hasSelectedVehicles()) {
                Swal.fire('Please select at least one vehicle in report settings.', '', 'info');
                return false;
            }
            if ((formData.plant_ids) && (formData.customer_ids) && (formData.consignee_ids) && (formData.sales_rep_ids) &&
            (formData.vehicle_ids) && (formData.product_ids) &&
            (formData.from_date) && (formData.upto_date) && (formData.report_format)) {
                return true;
            }
            Swal.fire("plant,customer,consignee,sales_rep,vehicle,product,from_date,upto_date,report format must not be empty.", "", "info");
            return false;
        };

        const prepareDispatchExport = async (formData) => {
            const response = await getDispatchReport(cookies, formData);
            let exportColumns;
            let exportRecords = [];
            let totalsRow = null;

            const reportTypeLabel = formData.report_type === 'condensed' ? 'Condensed' : 'Detailed';
            const formatLabel = String(formData.report_format || '').replace(/_/g, ' ');
            const infoLines = [
                `Period: ${formData.from_date} to ${formData.upto_date}`,
                `Format: ${formatLabel} (${reportTypeLabel})`,
            ];

            if (formData.report_format === 'plantwise_abstract') {
                const cols = selectedAbstractPlantColumns.length
                    ? selectedAbstractPlantColumns
                    : (response.plant_columns || []);
                exportColumns = [
                    { fieldName: 'delivery_challan_date', headerName: 'Date' },
                    ...cols.map((col) => ({
                        fieldName: col.field,
                        headerName: formatAbstractPlantHeader(col.label),
                        align: 'right',
                    })),
                    { fieldName: 'total_qty', headerName: 'Total', align: 'right' },
                ];
                const abstractRows = (response.dispatch_report_list || []).map((row) => (
                    mapPlantwiseAbstractRowForExport(row, cols)
                ));
                const totalRow = abstractRows.find((row) => row.is_total);
                exportRecords = abstractRows.filter((row) => !row.is_total);
                if (totalRow) {
                    totalsRow = buildTotalsRow(exportColumns, {
                        delivery_challan_date: totalRow.delivery_challan_date || 'TOTAL',
                        total_qty: totalRow.total_qty,
                        ...cols.reduce((acc, col) => {
                            acc[col.field] = totalRow[col.field];
                            return acc;
                        }, {}),
                    });
                }
            } else {
                let exportRows = sortDispatchRows(
                    (response.dispatch_report_list || []).map(mapDispatchReportRow),
                );
                if (formData.report_type === 'condensed') {
                    exportRows = sortDispatchRows(aggregateCondensedDispatchRows(exportRows));
                }
                if (formData.report_format !== 'all') {
                    exportRows = buildGroupedReportRows(exportRows, formData.report_format, formData.report_type);
                } else {
                    exportRows = exportRows.map((dc) => ({
                        plant_alias: dc.plant_alias,
                        salesrep_alias: dc.salesrep_alias,
                        delivery_challan_no: dc.delivery_challan_no,
                        delivery_challan_date: dc.delivery_challan_date,
                        delivery_challan_time: dc.delivery_challan_time,
                        customer_name: dc.customer_name,
                        project_name: dc.project_name,
                        dc_grade: dc.dc_grade,
                        dc_qty: dc.dc_qty,
                        rate: dc.rate,
                        amount: dc.amount,
                        delivery_mode: dc.delivery_mode,
                        vehicle_no: dc.vehicle_no,
                        pump: dc.pump,
                        gang: dc.gang,
                        total_km: dc.total_km,
                        invoice_number: dc.invoice_number,
                    }));
                }
                exportRecords = exportRows;
                exportColumns = formData.report_type === 'condensed'
                    ? getCondensedColumns(formData.report_format)
                    : DISPATCH_REPORT_ALL_COLUMNS;

                const detailRows = exportRows.filter((row) => !row.is_group_header);
                const qtyTotal = detailRows.reduce((sum, row) => sum + Number(row.dc_qty || 0), 0);
                const amountTotal = detailRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
                totalsRow = buildTotalsRow(exportColumns, {
                    dc_qty: qtyTotal,
                    amount: amountTotal,
                    rate: qtyTotal ? amountTotal / qtyTotal : '',
                });
            }

            return {
                exportColumns,
                exportRecords,
                totalsRow,
                infoLines,
                plantIds: getSelectedPlantsForReport().map((plant) => plant.value),
            };
        };

        const handleDownloadExcel = async () => {
            const formData = constructFormData();
            if (!canExportDispatch(formData)) {
                return;
            }
            setIsLoading(true);
            try {
                const prepared = await prepareDispatchExport(formData);
                await exportReportWithPlantHeader({
                    cookies,
                    plantIds: prepared.plantIds,
                    columns: prepared.exportColumns,
                    records: prepared.exportRecords,
                    filename: 'dispatch_report',
                    documentTitle: 'Dispatch Report',
                    infoLines: prepared.infoLines,
                    totalsRow: prepared.totalsRow,
                });
            } catch (error) {
                displayApiError(error);
            } finally {
                setIsLoading(false);
            }
        };

        const handleDownloadCsv = async () => {
            const formData = constructFormData();
            if (!canExportDispatch(formData)) {
                return;
            }
            setIsLoading(true);
            try {
                const prepared = await prepareDispatchExport(formData);
                await exportReportAsCsvWithPlantHeader({
                    cookies,
                    plantIds: prepared.plantIds,
                    columns: prepared.exportColumns,
                    records: prepared.exportRecords,
                    filename: 'dispatch_report',
                    documentTitle: 'Dispatch Report',
                    infoLines: prepared.infoLines,
                    totalsRow: prepared.totalsRow,
                });
            } catch (error) {
                displayApiError(error);
            } finally {
                setIsLoading(false);
            }
        };

    return (
            <div className="report-embedded-page">
                {!filtersVisible ? (
                    <div className="container-fluid px-3">
                        <ReportFilterToggleBar
                            onShow={() => setFiltersVisible(true)}
                            onSettings={openReportSettings}
                        />
                    </div>
                ) : null}
                {filtersVisible ? (
                <form className="form" onSubmit={handleSubmit}>
                    <div className="container-fluid px-3">
                       <Row> 
                            <Col xl={12} lg={12} md={12}>
                                <div className="report-legacy-filters form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <div className="report-legacy-filters-fields">
                                        <ReactSelect
                                            options={customersWithSelectAll}
                                            isMulti={true}
                                            closeMenuOnSelect={false}
                                            hideSelectedOptions={false}
                                            components={{Option, ValueContainer}}
                                            isClearable={true}
                                            value={selectedCustomer}
                                            onChange={onCustomerSelect}
                                            className="react-select-dropdown col-sm-3"
                                            placeholder="Please select Customer"
                                        />
                                        <ReactSelect
                                            options={sitesWithSelectAll}
                                            isMulti={true}
                                            closeMenuOnSelect={false}
                                            hideSelectedOptions={false}
                                            components={{Option, ValueContainer}}
                                            isClearable={true}
                                            value={selectedSite}
                                            onChange={onSiteSelect}
                                            className="react-select-dropdown col-sm-3"
                                            placeholder="Please Select Site"
                                        />
                                        <ReactSelect
                                            options={gradesWithSelectAll}
                                            isMulti={true}
                                            closeMenuOnSelect={false}
                                            hideSelectedOptions={false}
                                            components={{Option, ValueContainer}}
                                            isClearable={true}
                                            value={selectedGrade}
                                            onChange={onGradeSelect}
                                            className="react-select-dropdown col-sm-3"
                                            placeholder="Please Select Grade"
                                        />
                                        </div>
                                        <div className="report-legacy-filters-row">
                                        <RangePicker
                                            value={[fromDate, toDate]}
                                            format="DD-MM-YYYY"
                                            onChange={handleDateChange}
                                            style={{width:"270px"}}
                                        />
                                        <select
                                            className="browser-default custom-select col-sm-3"
                                            onChange={handleChange}
                                            value={inputs.report_format || 'datewise'}
                                            name="report_format"
                                            style={{width:"200px"}}
                                        >
                                            <option value="all">All</option>
                                            <option value="datewise">Datewise</option>
                                            <option value="customerwise">Customerwise</option>
                                            <option value="marketingwise">Marketingwise</option>
                                            <option value="plantwise_abstract">Plantwise - Abstract</option>
                                        </select>
                                        <div className="report-legacy-actions">
                                        <SettingsIcon onClickHandler={openReportSettings} variant="inline" />
                                        <Button
                                            color="primary"
                                            className="list-page-toolbar-generate-btn"
                                            type="button"
                                            onClick={handleGenerate}
                                            disabled={isLoading}
                                        >
                                            Generate
                                        </Button>
                                        </div>
                                        </div>
                                </div>
                             </Col>
                        </Row>
                    </div> 
                </form>
                ) : null}
                <Modal isOpen={showSettingsModal} toggle={() => setShowSettingsModal(false)} centered>
                    <ModalHeader toggle={() => setShowSettingsModal(false)}>Report Settings</ModalHeader>
                    <ModalBody>
                        <div className="mb-3">
                            <label className="font-weight-bold d-block mb-2">Plant</label>
                            <ReactSelect
                                options={plantsWithSelectAll}
                                isMulti={true}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                components={{ Option, ValueContainer }}
                                isClearable={true}
                                value={settingsDraft.selectedPlant}
                                onChange={onSettingsPlantSelect}
                                className="react-select-dropdown"
                                placeholder="Please select Plant"
                            />
                        </div>
                        <div className="mb-3">
                            <label className="font-weight-bold d-block mb-2">Sales Rep</label>
                            <ReactSelect
                                options={salesrepsWithSelectAll}
                                isMulti={true}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                components={{ Option, ValueContainer }}
                                isClearable={true}
                                value={settingsDraft.selectedSalesrep}
                                onChange={onSettingsSalesRepSelect}
                                className="react-select-dropdown"
                                placeholder="Please select Sales Rep"
                            />
                        </div>
                        <div className="mb-3">
                            <label className="font-weight-bold d-block mb-2">Vehicle</label>
                            <ReactSelect
                                options={vehiclesWithSelectAll}
                                isMulti={true}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                components={{ Option, ValueContainer }}
                                isClearable={true}
                                value={settingsDraft.selectedVehicle}
                                onChange={onSettingsVehicleSelect}
                                className="react-select-dropdown"
                                placeholder="Please select Vehicle"
                            />
                        </div>
                        <div>
                            <label className="font-weight-bold d-block mb-2">Report Type</label>
                            <select
                                className="browser-default custom-select"
                                value={settingsDraft.report_type}
                                onChange={(event) => setSettingsDraft((current) => ({
                                    ...current,
                                    report_type: event.target.value,
                                }))}
                            >
                                <option value="detailed">Detailed</option>
                                <option value="condensed">Condensed</option>
                            </select>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
                        <Button color="primary" onClick={applyReportSettings}>Apply</Button>
                    </ModalFooter>
                </Modal>
                <div className="container-fluid px-3">
                        {data.records.length > 0 ? (
                                <div className="list-page-summary-bar">
                                    <div className="list-page-summary-primary">
                                        <span className="list-page-summary-label">FILTERED VIEW</span>
                                        <span className="list-page-summary-count">
                                            {data.records.filter((row) => !row.is_group_header).length}{' '}
                                            {data.records.filter((row) => !row.is_group_header).length === 1 ? 'record' : 'records'}
                                        </span>
                                    </div>
                                    <div className="list-page-summary-metrics">
                                        <div className="list-page-metric-card">
                                            <span className="list-page-metric-label">Total DC Qty</span>
                                            <span className="list-page-metric-value is-yellow">{totalDcQty}</span>
                                        </div>
                                        {!isPlantwiseAbstract ? (
                                        <>
                                        <div className="list-page-metric-card">
                                            <span className="list-page-metric-label">Total Amount</span>
                                            <span className="list-page-metric-value">{totalAmount}</span>
                                        </div>
                                        <div className="list-page-metric-card">
                                            <span className="list-page-metric-label">Deliveries</span>
                                            <span className="list-page-metric-value is-b2b">
                                                {data.records.filter((row) => !row.is_group_header && !isInactiveRecord(row)).length}
                                            </span>
                                        </div>
                                        <div
                                            className="list-page-metric-card is-cancelled-qty"
                                            onClick={showCancelledDcDetails}
                                            style={{ cursor: 'pointer' }}
                                            title="Click to view cancelled delivery challans"
                                        >
                                            <span className="list-page-metric-label">Cancelled</span>
                                            <span className="list-page-metric-value is-cancelled">{cancelledDcQty}</span>
                                            <span className="list-page-metric-subvalue">{cancelledDcCount} DC cancelled</span>
                                        </div>
                                        </>
                                        ) : (
                                        <div className="list-page-metric-card">
                                            <span className="list-page-metric-label">Days</span>
                                            <span className="list-page-metric-value is-b2b">
                                                {Math.max(data.records.filter((r) => !r.is_total).length, 0)}
                                            </span>
                                        </div>
                                        )}
                                    </div>
                                </div>
                        ) : null}
                    <div className="list-page-table-wrap">
                        <div className="item-list-table-container">
                            <div className="table-responsive">
                                <ReactTableWrapper
                                    title={isPlantwiseAbstract ? 'Dispatch Report - Plantwise Abstract' : 'Dispatch Report'}
                                    columns={columns}
                                    data={data.records}
                                    exportFilename="dispatch_report"
                                    enableExportToolbar
                                    onExcelExport={handleDownloadExcel}
                                    onCSVExport={handleDownloadCsv}
                                    onPDFExport={handleDownloadPdf}
                                    onImageExport={handleImageExport}
                                    onSettingsClick={openReportSettings}
                                    isLoading={isLoading}
                                    left_pinned={isPlantwiseAbstract ? ['delivery_challan_date'] : undefined}
                                    right_pinned={isPlantwiseAbstract ? [] : undefined}
                                    compactColumns={isPlantwiseAbstract}
                                    enableSorting={!isPlantwiseAbstract}
                                    getRowSx={(row) => {
                                        if (row.is_group_header || row.is_total) {
                                            return {
                                                backgroundColor: '#dbeafe !important',
                                                '&:hover': { backgroundColor: '#bfdbfe !important' },
                                                '& td': { borderTop: '2px solid #2563eb' },
                                            };
                                        }
                                        return {};
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
);
}

export default DispatchReportTable;